import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { useQuery } from "@msuite/ui/query";
import { WorkspaceTag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import { getAddressString, guardString } from "@msuite/utils/strings";
import { lazy, Suspense } from "react";
import { Can } from "@/components/citadel";
import { NoteText } from "@/components/notes";
import { ProjectSummary } from "@/components/projects";
import { ProjectInvoiceOverview } from "@/components/projects/project-invoice-overview";
import { client } from "@/hono";
import type { ProjectPanelBaseProps } from "../types";

const ProjectMap = lazy(() => import("@/components/projects/project-map"));
const Notes = lazy(() => import("@/components/notes"));

/** Props Interface */
interface ProjectGeneralProps extends ProjectPanelBaseProps {}

export function ProjectGeneral({ projectId }: ProjectGeneralProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { data: project } = useQuery({
		queryKey: ["projects", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.project;
		},
	});

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<div className="flex flex-col gap-4">
					<Suspense fallback={<LazyFallback variant="portal" />}>
						<ContentCard
							icon={icon("Info")}
							title={t("general.information")}
						>
							<DataGrid>
								<DataGrid.Row>
									<DataGrid.Label>{t("organization.workspace")}</DataGrid.Label>
									<DataGrid.Value>
										<WorkspaceTag workspace={project?._workspace} />
									</DataGrid.Value>
								</DataGrid.Row>
								<DataGrid.Row>
									<DataGrid.Label>{t("address.address")}</DataGrid.Label>
									<DataGrid.Value>{getAddressString(project)}</DataGrid.Value>
								</DataGrid.Row>
								{project?._relatedProject && (
									<DataGrid.Row>
										<DataGrid.Label>{t("projects.fields.related_project")}</DataGrid.Label>
										<DataGrid.Value>
											<Link href={`/projects/${project._relatedProject.id}`}>
												{project._relatedProject.name}
											</Link>
										</DataGrid.Value>
									</DataGrid.Row>
								)}
								<DataGrid.Row>
									<DataGrid.Label>{t("notes.note")}</DataGrid.Label>
									<DataGrid.Value>
										<NoteText note={guardString(project?.note)} />
									</DataGrid.Value>
								</DataGrid.Row>
							</DataGrid>
						</ContentCard>
						{/* The chart reads `GET /statistics/invoices`, which the server gates on
						    `read Invoice` — a rule a member does not hold. The whole card is wrapped,
						    not just the chart, so a denied caller sees no revenue panel rather than an
						    empty one, and the query behind it never fires. */}
						<Can
							action="read"
							subject="Invoice"
						>
							<ContentCard
								title={t("invoices.invoices")}
								icon={icon("Activity")}
							>
								<ProjectInvoiceOverview projectId={projectId} />
							</ContentCard>
						</Can>
						<ProjectMap project={project} />
					</Suspense>
				</div>
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<ProjectSummary projectId={projectId} />
				<Suspense fallback={<LazyFallback variant="inline" />}>
					<Notes
						notesTable="projectNotes"
						identifier={project?.id ?? ""}
						parent={project}
					/>
				</Suspense>
			</Panels.Panel>
		</Panels>
	);
}
